const GRID_SELECTOR = '.product-grid';
const ITEM_SELECTOR = '.grid__item';
const TITLE_SELECTOR = '[data-card-title]';
const ROW_TOP_TOLERANCE_PX = 4;

let resizeListenerAttached = false;
let gridResizeObserver: ResizeObserver | null = null;
let gridMutationObserver: MutationObserver | null = null;
let equalizeFrame = 0;

function groupItemsByRow(items: HTMLElement[]): HTMLElement[][] {
  const sorted = [...items].sort(
    (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top,
  );

  const rows: HTMLElement[][] = [];

  for (const item of sorted) {
    const top = item.getBoundingClientRect().top;
    const currentRow = rows[rows.length - 1];

    if (!currentRow) {
      rows.push([item]);
      continue;
    }

    const rowTop = currentRow[0].getBoundingClientRect().top;
    if (Math.abs(top - rowTop) <= ROW_TOP_TOLERANCE_PX) {
      currentRow.push(item);
    } else {
      rows.push([item]);
    }
  }

  return rows;
}

function equalizeRowTitles(titles: HTMLElement[]): void {
  titles.forEach((title) => {
    title.style.minHeight = '';
  });

  const maxHeight = Math.max(0, ...titles.map((title) => title.offsetHeight));
  if (maxHeight === 0) return;

  titles.forEach((title) => {
    title.style.minHeight = `${maxHeight}px`;
  });
}

export function equalizeCardTitleHeights(): void {
  document.querySelectorAll<HTMLElement>(GRID_SELECTOR).forEach((grid) => {
    const items = Array.from(grid.querySelectorAll<HTMLElement>(ITEM_SELECTOR));
    if (items.length === 0) return;

    const rows = groupItemsByRow(items);

    for (const rowItems of rows) {
      const titles = rowItems
        .map((item) => item.querySelector<HTMLElement>(TITLE_SELECTOR))
        .filter((title): title is HTMLElement => title !== null);

      if (titles.length > 1) {
        equalizeRowTitles(titles);
      }
    }
  });
}

function scheduleEqualizeCardTitleHeights(): void {
  if (equalizeFrame) cancelAnimationFrame(equalizeFrame);

  equalizeFrame = requestAnimationFrame(() => {
    equalizeFrame = 0;
    equalizeCardTitleHeights();
  });
}

function observeGrid(grid: HTMLElement): void {
  if (grid.dataset.titleHeightsObserved === 'true') return;
  grid.dataset.titleHeightsObserved = 'true';

  gridResizeObserver?.observe(grid);
  gridMutationObserver?.observe(grid, { childList: true, subtree: true });
}

function observeProductGrids(): void {
  document.querySelectorAll<HTMLElement>(GRID_SELECTOR).forEach(observeGrid);
}

function initGridObservers(): void {
  if (gridResizeObserver || gridMutationObserver) return;

  const handleChange = () => {
    observeProductGrids();
    scheduleEqualizeCardTitleHeights();
  };

  gridResizeObserver = new ResizeObserver(handleChange);
  gridMutationObserver = new MutationObserver(handleChange);

  observeProductGrids();

  const collectionGrid = document.getElementById('CollectionProductGrid');
  if (collectionGrid) {
    gridMutationObserver.observe(collectionGrid, { childList: true, subtree: true });
  }

  const bodyObserver = new MutationObserver(() => {
    observeProductGrids();
    scheduleEqualizeCardTitleHeights();
  });
  bodyObserver.observe(document.body, { childList: true, subtree: true });
}

export function initEqualizeCardTitleHeights(): void {
  const run = () => scheduleEqualizeCardTitleHeights();

  initGridObservers();
  run();

  if (!resizeListenerAttached) {
    window.addEventListener('resize', run);
    resizeListenerAttached = true;
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(run).catch(() => undefined);
  }
}
